/**
 * Campaign list URL-owned filter/pagination state (task 8.1, design.md §3
 * "State restoration contract" point 1: "Filters, sort..., `page`, and `size`
 * are URL search parameters and are the source of truth. Invalid values are
 * normalized with `replace`, not added to history.").
 *
 * Deviation disclosed explicitly: `q` (free-text search) is intentionally
 * NOT part of this list's URL-owned state. design.md §5.2 itself says
 * "search only if backed by an API parameter", and the real backend contract
 * (`AdminCampaignQuery` in `AdminCampaignModels.cs`, read by
 * `AdminCampaignsFunction.List`) accepts only `estado`/`page`/`size` — there
 * is no `q` parameter anywhere server-side. Inventing a client-side-only `q`
 * filter (e.g. filtering only the current page's already-fetched rows) would
 * silently misrepresent a full-dataset search as one that only searches
 * whatever happened to be on the current page — worse than not offering
 * search at all. If/when a real `q` parameter ships server-side, this module
 * is the one place to add it.
 */

/** The 7 known `campaign.estado` lifecycle values (design.md §5.1 item 2 / `formatting/status.ts#LIFECYCLE_STATE_ORDER`). */
const KNOWN_ESTADO_VALUES = new Set([
  "borrador",
  "programada",
  "enviando",
  "parcial",
  "fallida",
  "completada",
  "cancelada",
]);

/** Matches the backend's own clamp (`AdminCampaignSqlBuilder.NormalizeSize`): `1..50`, default `20`. */
const MIN_SIZE = 1;
const MAX_SIZE = 50;
const DEFAULT_SIZE = 20;
const DEFAULT_PAGE = 1;

/** Only these query keys are ever read/written by the campaign list — the same allow-list is reused to carry list context onto detail/monitoring/telemetry links (design.md §3 point 2). */
export const CAMPAIGN_LIST_ALLOWED_KEYS = ["estado", "page", "size"] as const;

export interface CampaignListParams {
  estado?: string;
  page: number;
  size: number;
}

export const DEFAULT_CAMPAIGN_LIST_PARAMS: CampaignListParams = {
  estado: undefined,
  page: DEFAULT_PAGE,
  size: DEFAULT_SIZE,
};

export interface NormalizedCampaignListParams {
  /** The corrected params — always internally valid, even if the input was not. */
  params: CampaignListParams;
  /** The URLSearchParams that should replace the current URL when `changed` is `true`. */
  normalized: URLSearchParams;
  /** `true` only when at least one input value was invalid and had to be corrected. */
  changed: boolean;
}

function normalizePage(raw: string | null): { value: number; changed: boolean } {
  if (raw === null) {
    return { value: DEFAULT_PAGE, changed: false };
  }
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return { value: DEFAULT_PAGE, changed: true };
  }
  return { value: parsed, changed: String(parsed) !== raw };
}

function normalizeSize(raw: string | null): { value: number; changed: boolean } {
  if (raw === null) {
    return { value: DEFAULT_SIZE, changed: false };
  }
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < MIN_SIZE || parsed > MAX_SIZE) {
    return { value: DEFAULT_SIZE, changed: true };
  }
  return { value: parsed, changed: String(parsed) !== raw };
}

function normalizeEstado(raw: string | null): { value: string | undefined; changed: boolean } {
  if (raw === null || raw === "") {
    return { value: undefined, changed: false };
  }
  if (!KNOWN_ESTADO_VALUES.has(raw)) {
    return { value: undefined, changed: true };
  }
  return { value: raw, changed: false };
}

/**
 * Reads `estado`/`page`/`size` from a URL's search params and returns the
 * corrected values plus a ready-to-use `URLSearchParams` for the cases where
 * the caller must call `setSearchParams(result.normalized, { replace: true })`
 * to correct the URL (design.md §3 point 1 — normalization never adds a
 * history entry). Unrelated/unknown keys in the input are ignored entirely —
 * this module owns only its own three keys.
 */
export function normalizeCampaignListSearchParams(searchParams: URLSearchParams): NormalizedCampaignListParams {
  const page = normalizePage(searchParams.get("page"));
  const size = normalizeSize(searchParams.get("size"));
  const estado = normalizeEstado(searchParams.get("estado"));

  const normalized = new URLSearchParams();
  if (estado.value !== undefined) {
    normalized.set("estado", estado.value);
  }
  normalized.set("page", String(page.value));
  normalized.set("size", String(size.value));

  return {
    params: { estado: estado.value, page: page.value, size: size.value },
    normalized,
    changed: page.changed || size.changed || estado.changed,
  };
}
